import { Flex } from '@mantine/core';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import { BubbleMenu, Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Markdown } from 'tiptap-markdown';

import AbsoluteError from '@/components/ui/AbsoluteError';
import { useBubbleMenuPlacement } from '@/hooks/useBubbleMenuPlacement';
import { useLocalStore } from '@/hooks/useLocalStore';
import { OpenStore } from '@/stores/local/shared/OpenStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import { useDevices } from '@/utils/devices';

import type { FieldTextareaProps } from '../FieldTextarea/FieldTextarea';

import FormattingToolbar from './FormattingToolbar';
import LinkController from './LinkController/LinkController';
import { TOOLBAR_GROUPS, DEFAULT_FLOATING_FORMATTERS, TABLE_FLOATING_FORMATTERS } from './config';
import CustomHardBreak from './extensions/CustomHardBreak';
import type { FormatType } from './types';
import { buildServerMarkdown, formatCommands } from './utils';

import s from './FieldTextareaWithEditingMode.module.scss';

export type FieldTextareaWithEditingModeProps = FieldTextareaProps & {
  store: FieldTextStore;
  formatters?: FormatType[];
  disabled?: boolean;
};

const FieldTextareaWithEditingMode: React.FC<FieldTextareaWithEditingModeProps> = ({
  store,
  formatters = DEFAULT_FLOATING_FORMATTERS,
  className,
  placeholder,
  disabled,
}) => {
  const isDisabled = disabled ?? store.disabled;
  const { isCompactViewport } = useDevices();
  const linkModalStore = useLocalStore(() => new OpenStore());
  const lastSyncedValue = React.useRef<string | null>(null);

  const editor = useEditor({
    editable: !isDisabled,
    extensions: [
      StarterKit.configure({
        hardBreak: false,
      }),
      CustomHardBreak,
      Underline,
      Highlight,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Placeholder.configure({
        placeholder: placeholder ?? '',
        showOnlyWhenEditable: true,
        includeChildren: true,
      }),
    ],
    onCreate({ editor }) {
      const initial = store.value ?? '';

      editor.commands.setContent(initial, true);
      lastSyncedValue.current = initial;
    },
    onUpdate({ editor }) {
      const md = buildServerMarkdown(editor);

      if (md !== store.value) {
        store.setValue(md);
        lastSyncedValue.current = md;
      }
    },
  });

  const { placement, offset } = useBubbleMenuPlacement({ isPhone: isCompactViewport, editor });

  // React to external store changes (e.g., form reset) — keep editor in sync
  React.useEffect(() => {
    if (!editor) {
      return;
    }
    const incoming = store.value ?? '';
    const current = buildServerMarkdown(editor);

    if (incoming === lastSyncedValue.current) {
      return;
    }

    if (incoming === current) {
      lastSyncedValue.current = incoming;

      return;
    }

    editor.commands.setContent(incoming, false);
    lastSyncedValue.current = incoming;
  }, [editor, store.value]);

  const uiState = useEditorState({
    editor,
    selector: (ctx) => {
      const isHeadingActive = ctx.editor?.isActive('heading') ?? false;
      const rawHeadingLevel = isHeadingActive ? ctx.editor?.getAttributes('heading')?.level : null;
      const headingLevel = typeof rawHeadingLevel === 'number' ? rawHeadingLevel : null;

      return {
        bold: ctx.editor?.isActive('bold') ?? false,
        italic: ctx.editor?.isActive('italic') ?? false,
        underline: ctx.editor?.isActive('underline') ?? false,
        strike: ctx.editor?.isActive('strike') ?? false,
        highlight: ctx.editor?.isActive('highlight') ?? false,
        headingLevel,
        bulletList: ctx.editor?.isActive('bulletList') ?? false,
        orderedList: ctx.editor?.isActive('orderedList') ?? false,
        blockquote: ctx.editor?.isActive('blockquote') ?? false,
        codeBlock: ctx.editor?.isActive('codeBlock') ?? false,
        link: ctx.editor?.isActive('link') ?? false,
        table: ctx.editor?.isActive('table') ?? false,
      };
    },
  });

  const floatingFormatters = React.useMemo<FormatType[]>(
    () => formatters.filter((item) => !TABLE_FLOATING_FORMATTERS.includes(item)),
    [formatters],
  );

  const handleApply = React.useCallback(
    (type: FormatType) => {
      if (!editor) {
        return;
      }

      if (type === 'link') {
        linkModalStore.open();

        return;
      }

      if (type === 'heading') {
        return;
      }

      editor.commands.focus();
      formatCommands[type]?.(editor);
    },
    [editor, linkModalStore],
  );

  const shouldShow = React.useCallback(
    ({ editor, from, to }: { editor: Editor; from: number; to: number }) => {
      if (!editor.isEditable || isDisabled) {
        return false;
      }

      return from !== to;
    },
    [isDisabled],
  );

  const isFormatterActive = React.useCallback(
    (type: FormatType) => {
      switch (type) {
        case 'bold':
          return !!uiState?.bold;
        case 'italic':
          return !!uiState?.italic;
        case 'underline':
          return !!uiState?.underline;
        case 'strike':
          return !!uiState?.strike;
        case 'highlight':
          return !!uiState?.highlight;
        case 'heading':
          return uiState?.headingLevel != null;
        case 'heading1':
          return uiState?.headingLevel === 1;
        case 'heading2':
          return uiState?.headingLevel === 2;
        case 'heading3':
          return uiState?.headingLevel === 3;
        case 'heading4':
          return uiState?.headingLevel === 4;
        case 'heading5':
          return uiState?.headingLevel === 5;
        case 'heading6':
          return uiState?.headingLevel === 6;
        case 'bulletList':
          return !!uiState?.bulletList;
        case 'orderedList':
          return !!uiState?.orderedList;
        case 'blockquote':
          return !!uiState?.blockquote;
        case 'codeBlock':
          return !!uiState?.codeBlock;
        case 'link':
          return !!uiState?.link;
        case 'table':
          return !!uiState?.table;
        default:
          return false;
      }
    },
    [uiState],
  );

  const stickyToolbarGroups = React.useMemo(() => {
    const groups = TOOLBAR_GROUPS.map((group) => {
      if (group.id !== 'insert') {
        return group;
      }

      if (!uiState?.table) {
        return {
          ...group,
          actions: isCompactViewport ? [] : group.actions,
        };
      }

      const tableActions: FormatType[] = ['table' as FormatType, ...TABLE_FLOATING_FORMATTERS];

      return {
        ...group,
        actions: isCompactViewport ? TABLE_FLOATING_FORMATTERS : tableActions,
      };
    });

    return groups.filter((group) => group.actions.length > 0);
  }, [isCompactViewport, uiState?.table]);

  const toolbarDisabled = !editor || isDisabled;

  return (
    <div className={cn(s.root, className, isDisabled && s.root_disabled)}>
      <div className={s['root__sticky-toolbar']}>
        <div className={s['root__toolbar-row']}>
          {stickyToolbarGroups.map((group, index) => {
            const showDivider = index < stickyToolbarGroups.length - 1;

            return (
              <React.Fragment key={group.id}>
                <div className={s['root__toolbar-group']}>
                  <FormattingToolbar
                    formatters={group.actions}
                    onClick={handleApply}
                    disabled={toolbarDisabled}
                    isActive={isFormatterActive}
                  />
                </div>
                {showDivider && <div className={s['root__toolbar-divider']} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className={cn(s.root__wrapper, store.hasError && s['root__wrapper-has-error'])}>
        {store.hasError && <AbsoluteError className={s.root__error} error={store.error} />}
        <EditorContent editor={editor} className={s.root__editor} />
      </div>

      {editor && (
        <BubbleMenu
          className={s['root__bubble-menu']}
          editor={editor}
          pluginKey="floating-formatting-toolbar"
          shouldShow={shouldShow}
          tippyOptions={{
            placement,
            offset,
            interactive: true,
            moveTransition: 'transform 0.12s ease',
            hideOnClick: false,
            // flip и preventOverflow включены по умолчанию в Tippy/Popper
          }}
        >
          <Flex align="center" gap={4}>
            <FormattingToolbar
              formatters={floatingFormatters}
              onClick={handleApply}
              disabled={!editor || isDisabled}
              isActive={isFormatterActive}
            />
          </Flex>
        </BubbleMenu>
      )}

      <LinkController editor={editor} opened={linkModalStore.isOpen} onClose={linkModalStore.close} />
    </div>
  );
};

export default observer(FieldTextareaWithEditingMode);
